import type { CompoundEntityRef, Entity } from '@backstage/catalog-model';
import {
  DOC_TYPE_ANNOTATION,
  PLAYBOOK_DOC_TYPE,
  RECORD_DOC_TYPE,
} from '@internal/plugin-neo4j-techdocs-common';
import { DOC_TYPES } from '../types';

/** The legacy Aura docs site, built from `docs/aura-docs/`. */
export const LEGACY_DOCS_ENTITY_REF: CompoundEntityRef = {
  kind: 'component',
  namespace: 'default',
  name: 'aura-docs',
};

/**
 * The name of the legacy site on every docs page: the docs sidebar section,
 * the breadcrumb, and the title of the site home. The page shows it at once,
 * before the entity of the site loads.
 */
export const LEGACY_DOCS_TITLE = 'Aura Docs (legacy)';

// The software-catalogue puts ADRs and component playbooks inside the docs
// site of their parent component, under these folders
// (components/software-catalogue/server/pkg/aura/process.go). The parent
// component has no doc type annotation, so the folder names the doc type.
const SUB_DOC_FOLDERS: Array<[folder: string, docType: string]> = [
  ['adr/', RECORD_DOC_TYPE],
  ['playbooks/', PLAYBOOK_DOC_TYPE],
];

// Mirror the TechDocs annotation keys without adding a dependency on
// @backstage/plugin-techdocs-common.
const DOCS_SITE_ANNOTATIONS = [
  'backstage.io/techdocs-ref',
  'backstage.io/techdocs-entity',
];

/**
 * Returns the doc type of a reader page, or `undefined` when the page is not
 * a category doc. `entity` is the entity of the docs site, `undefined` while
 * it loads. `path` is the page path inside the site.
 */
export function resolveDocType(
  entity: Entity | undefined,
  path: string,
): string | undefined {
  if (!entity) {
    return undefined;
  }

  const annotatedDocType = entity.metadata.annotations?.[DOC_TYPE_ANNOTATION];
  if (annotatedDocType) {
    // A doc entity: its annotation decides. The legacy site has the doc type
    // `aura-docs`, which is not a category.
    return DOC_TYPES[annotatedDocType] ? annotatedDocType : undefined;
  }

  const pagePath = path.replace(/^\/+/, '');
  return SUB_DOC_FOLDERS.find(([folder]) => pagePath.startsWith(folder))?.[1];
}

/**
 * True when the entity itself is a category doc: its doc type annotation names
 * a category. False for the legacy site and for a parent component that holds
 * ADRs or playbooks in its docs site.
 */
export function isCategoryDocEntity(entity: Entity | undefined): boolean {
  const docType = entity?.metadata.annotations?.[DOC_TYPE_ANNOTATION];
  return Boolean(docType && DOC_TYPES[docType]);
}

/** Includes both an entity's own docs site and docs hosted by another entity. */
export function hasDocsSite(entity: Entity): boolean {
  return DOCS_SITE_ANNOTATIONS.some(
    annotation => entity.metadata.annotations?.[annotation],
  );
}

export function isLegacyDocsEntity(entityRef: CompoundEntityRef): boolean {
  return (
    entityRef.kind.toLowerCase() === LEGACY_DOCS_ENTITY_REF.kind &&
    entityRef.namespace.toLowerCase() === LEGACY_DOCS_ENTITY_REF.namespace &&
    entityRef.name.toLowerCase() === LEGACY_DOCS_ENTITY_REF.name
  );
}
